/**
 * Task Filters Component
 * Filtering options for the Kanban board
 */

import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, X, Tag, Check, ChevronsUpDown, CalendarClock, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import type { KanbanQueryParams, TaskPriority, TaskStatus, DueDatePreset } from '../types';
import { TASK_PRIORITY_LABELS, TASK_STATUS_LABELS, TASK_STATUS_ORDER, DUE_DATE_PRESET_LABELS } from '../types';
import type { QuoteOption } from '../api';

interface TaskFiltersProps {
  filters: KanbanQueryParams;
  onFiltersChange: (filters: KanbanQueryParams) => void;
  quotes?: QuoteOption[];
  assignees?: Array<{ id: string; name: string; email?: string }>;
}

export function TaskFilters({
  filters,
  onFiltersChange,
  quotes = [],
  assignees = [],
}: TaskFiltersProps) {
  const [tagInput, setTagInput] = useState('');
  const [quoteComboboxOpen, setQuoteComboboxOpen] = useState(false);
  const [assigneeComboboxOpen, setAssigneeComboboxOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);

  const { user } = useSession();
  // Resolve the current user's assignee-list id by email. The assignee dropdown
  // is sourced from legacy users (Mongo _id), while session.user._id is the
  // pg_id — matching by email avoids the id-shape mismatch.
  const myAssigneeId = user?.email
    ? assignees.find((a) => a.email && a.email.toLowerCase() === user.email.toLowerCase())?.id
    : undefined;
  const isFilteredToMe = !!myAssigneeId && filters.assignedToId === myAssigneeId;

  const handleToggleMyTasks = () => {
    if (!myAssigneeId) return;
    onFiltersChange({
      ...filters,
      assignedToId: isFilteredToMe ? undefined : myAssigneeId,
    });
  };

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined });
  };

  const handleQuoteChange = (value: string) => {
    onFiltersChange({
      ...filters,
      quoteId: value === 'all' ? undefined : value,
      projectId: undefined,
    });
  };

  const handleAssigneeChange = (value: string) => {
    onFiltersChange({ ...filters, assignedToId: value === 'all' ? undefined : value });
  };

  const handleStatusToggle = (status: TaskStatus) => {
    const current = filters.statuses || [];
    const updated = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    onFiltersChange({ ...filters, statuses: updated.length ? updated : undefined });
  };

  const handlePriorityChange = (value: string) => {
    onFiltersChange({
      ...filters,
      priority: value === 'all' ? undefined : (value as TaskPriority),
    });
  };

  const handleDueDateChange = (value: string) => {
    onFiltersChange({
      ...filters,
      dueDatePreset: value === 'all' ? undefined : (value as DueDatePreset),
    });
  };

  const handleTagInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const tag = tagInput.trim().toLowerCase();
        if (tag && !filters.tags?.includes(tag)) {
          onFiltersChange({
            ...filters,
            tags: [...(filters.tags || []), tag],
          });
        }
        setTagInput('');
      }
    },
    [tagInput, filters, onFiltersChange]
  );

  const removeTag = useCallback(
    (tagToRemove: string) => {
      const newTags = filters.tags?.filter((t) => t !== tagToRemove);
      onFiltersChange({
        ...filters,
        tags: newTags?.length ? newTags : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const clearFilters = () => {
    onFiltersChange({});
    setTagInput('');
  };

  const hasActiveFilters = Boolean(
    filters.search ||
      filters.quoteId ||
      filters.assignedToId ||
      filters.priority ||
      filters.statuses?.length ||
      filters.dueDatePreset ||
      filters.tags?.length
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px] max-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            value={filters.search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* My Tasks quick toggle */}
        {myAssigneeId && (
          <Button
            variant={isFilteredToMe ? 'secondary' : 'outline'}
            size="default"
            onClick={handleToggleMyTasks}
            aria-pressed={isFilteredToMe}
            title={isFilteredToMe ? 'Showing tasks assigned to you — click to clear' : 'Show only tasks assigned to you'}
          >
            <UserRound className="h-4 w-4 mr-2" />
            My Tasks
          </Button>
        )}

        {/* Quote Filter - Searchable Combobox */}
        {quotes.length > 0 && (
          <Popover open={quoteComboboxOpen} onOpenChange={setQuoteComboboxOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={quoteComboboxOpen}
                className="w-[180px] justify-between font-normal"
              >
                <span className="truncate">
                  {filters.quoteId
                    ? (() => {
                        const quote = quotes.find((q) => q.id === filters.quoteId);
                        if (!quote) return 'All Quotes';
                        return quote.quote_number;
                      })()
                    : 'All Quotes'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search quotes..." />
                <CommandList>
                  <CommandEmpty>No quote found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        handleQuoteChange('all');
                        setQuoteComboboxOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          !filters.quoteId ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      All Quotes
                    </CommandItem>
                    {quotes.map((quote) => {
                      const label = quote.account_name || quote.project_name || quote.quote_number;
                      return (
                        <CommandItem
                          key={quote.id}
                          value={`${quote.quote_number} ${label}`}
                          onSelect={() => {
                            handleQuoteChange(quote.id);
                            setQuoteComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              filters.quoteId === quote.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="font-mono">{quote.quote_number}</span>
                            {label !== quote.quote_number && (
                              <span className="text-xs text-muted-foreground truncate">
                                {label}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {/* Assignee Filter - Searchable Combobox */}
        {assignees.length > 0 && (
          <Popover open={assigneeComboboxOpen} onOpenChange={setAssigneeComboboxOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={assigneeComboboxOpen}
                className="w-[170px] justify-between font-normal"
              >
                <span className="truncate">
                  {filters.assignedToId
                    ? assignees.find((a) => a.id === filters.assignedToId)?.name || 'All Assignees'
                    : 'All Assignees'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search assignees..." />
                <CommandList>
                  <CommandEmpty>No assignee found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        handleAssigneeChange('all');
                        setAssigneeComboboxOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          !filters.assignedToId ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      All Assignees
                    </CommandItem>
                    {assignees.map((assignee) => (
                      <CommandItem
                        key={assignee.id}
                        value={assignee.name}
                        onSelect={() => {
                          handleAssigneeChange(assignee.id);
                          setAssigneeComboboxOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            filters.assignedToId === assignee.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {assignee.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {/* Status Filter - Multi-select */}
        <Popover open={statusPopoverOpen} onOpenChange={setStatusPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={statusPopoverOpen}
              className="w-[140px] justify-between font-normal"
            >
              <span className="truncate">
                {filters.statuses?.length
                  ? `Status (${filters.statuses.length})`
                  : 'All Statuses'}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0" align="start">
            <Command>
              <CommandList>
                <CommandGroup>
                  {TASK_STATUS_ORDER.map((status) => (
                    <CommandItem
                      key={status}
                      value={status}
                      onSelect={() => handleStatusToggle(status)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          filters.statuses?.includes(status) ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {TASK_STATUS_LABELS[status]}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Priority Filter */}
        <Select value={filters.priority || 'all'} onValueChange={handlePriorityChange}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {(Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[]).map((priority) => (
              <SelectItem key={priority} value={priority}>
                {TASK_PRIORITY_LABELS[priority]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Due Date Filter */}
        <Select value={filters.dueDatePreset || 'all'} onValueChange={handleDueDateChange}>
          <SelectTrigger className="w-[140px]">
            <CalendarClock className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Due Date" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Due Dates</SelectItem>
            {(Object.keys(DUE_DATE_PRESET_LABELS) as DueDatePreset[]).map((preset) => (
              <SelectItem key={preset} value={preset}>
                {DUE_DATE_PRESET_LABELS[preset]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Tag Filter Input */}
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by tag..."
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagInputKeyDown}
            className="pl-9 w-[140px]"
          />
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Active Tag Filters */}
      {filters.tags && filters.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Tags:</span>
          {filters.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
